// AccesoIconoModal.js — El acceso del header «con icono» (Learning): un modal
// propio, oscuro, con dos vistas en el mismo lugar: entrar y «¿Olvidaste tu
// contraseña?». La recuperación se pide aquí mismo (recuperar-contrasena.php,
// acción `solicitar`) sin mandar a la persona a otra pantalla.
//
// Los colores salen del header que lo abre (fondo al hacer scroll y color del
// botón de registro), así que no sabe nada de Learning.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authService, recuperarContrasenaService, API_BASE_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const aparecer = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const subir = keyframes`from { opacity: 0; transform: translateY(14px) scale(0.985); } to { opacity: 1; transform: none; }`;
const subirHoja = keyframes`from { transform: translateY(100%); } to { transform: none; }`;
const cambiar = keyframes`from { opacity: 0; transform: translateX(10px); } to { opacity: 1; transform: none; }`;

const Fondo = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(2, 5, 10, 0.72);
  backdrop-filter: blur(10px) saturate(120%);
  -webkit-backdrop-filter: blur(10px) saturate(120%);
  animation: ${aparecer} 0.2s ease-out;
  @media (max-width: 600px) { place-items: end center; padding: 0; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Panel = styled.div`
  --acento: ${p => p.$acento};
  --fondo-panel: ${p => p.$fondo};
  position: relative;
  width: min(420px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 40px 34px 30px;
  border-radius: 16px;
  color: #eef2f7;
  font-family: ${p => p.$fuente || 'inherit'};
  background:
    radial-gradient(120% 70% at 0% 0%, color-mix(in srgb, var(--acento) 14%, transparent), transparent 60%),
    ${p => p.$fondo};
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55);
  animation: ${subir} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
  @media (max-width: 600px) {
    width: 100%;
    max-height: 92dvh;
    border-radius: 18px 18px 0 0;
    padding: 34px 20px calc(24px + env(safe-area-inset-bottom));
    animation: ${subirHoja} 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
  ${p => p.$enPagina && css`
    width: 100%; max-height: none; overflow: visible; padding: 0; border: 0; border-radius: 0;
    background: none; box-shadow: none; animation: none;
    @media (max-width: 600px) { width: 100%; max-height: none; border-radius: 0; padding: 0; animation: none; }
  `}
`;

const Cerrar = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.06);
  color: #c3ccd9;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  svg { width: 16px; height: 16px; }
`;

// Pestañas «Iniciar sesión» / «Crear cuenta».
const Pestanas = styled.div`
  display: flex;
  margin: ${p => (p.$enPagina ? '0 0 28px' : '-6px 44px 26px 0')};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  button {
    flex: 1;
    padding: 0 0 12px;
    border: 0;
    background: none;
    color: #8b97a9;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    position: relative;
    transition: color 0.15s;
    &:hover { color: #fff; }
    &[aria-selected='true'] { color: #fff; }
    &[aria-selected='true']::after {
      content: '';
      position: absolute;
      left: 0; right: 0; bottom: -1px;
      height: 2px;
      border-radius: 2px;
      background: var(--acento);
    }
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; border-radius: 4px; }
  }
`;

const Vista = styled.div`
  animation: ${cambiar} 0.22s ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Titulo = styled.h2`
  margin: 0 0 8px;
  font-size: clamp(1.7rem, 6vw, 2.05rem);
  font-weight: 700;
  font-stretch: 78%;
  line-height: 1.02;
  letter-spacing: -0.01em;
  color: #fff;
`;

const Texto = styled.p`
  margin: 0 0 26px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #9aa6b8;
  max-width: 34ch;
`;

const Campo = styled.label`
  display: block;
  margin-bottom: 16px;
  > span {
    display: block;
    margin-bottom: 7px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #b7c1cf;
  }
`;

const Caja = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid ${p => (p.$error ? '#f07a7a' : 'rgba(255, 255, 255, 0.11)')};
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  &:focus-within i { color: var(--acento); }
  &:focus-within {
    border-color: var(--acento);
    background: rgba(255, 255, 255, 0.07);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--acento) 22%, transparent);
  }
  input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 14px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #fff;
    font: inherit;
    font-size: 1rem;
    &::placeholder { color: #5f6b7d; }
    &:-webkit-autofill {
      -webkit-text-fill-color: #fff;
      -webkit-box-shadow: 0 0 0 40px color-mix(in srgb, var(--fondo-panel) 88%, #fff) inset;
      caret-color: #fff;
    }
  }
`;

const Ico = styled.i`
  display: grid; place-items: center; flex: none; padding-left: 14px; color: #7d8796; transition: color 0.15s;
  svg { width: 18px; height: 18px; }
  & + input { padding-left: 10px !important; }
`;
const Mayus = styled.p`
  display: flex; align-items: center; gap: 6px; margin: 7px 0 0; font-size: 0.8rem; color: #f5c46b;
  svg { width: 14px; height: 14px; }
`;
const girar = keyframes`to { transform: rotate(360deg); }`;
const Giro = styled.span`
  display: inline-block; width: 16px; height: 16px; margin-right: 9px; vertical-align: -3px; border-radius: 50%;
  border: 2px solid currentColor; border-right-color: transparent; animation: ${girar} 0.7s linear infinite;
  @media (prefers-reduced-motion: reduce) { animation-duration: 2s; }
`;
const latir = keyframes`0% { transform: scale(.6); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); }`;
const Bienvenida = styled.div`
  text-align: center; padding: 28px 0 10px;
  .check { display: grid; place-items: center; width: 76px; height: 76px; margin: 0 auto 18px; border-radius: 50%;
    background: var(--acento); color: #fff; box-shadow: 0 16px 40px -14px var(--acento); animation: ${latir} 0.45s ease-out both; }
  .check svg { width: 36px; height: 36px; }
  @media (prefers-reduced-motion: reduce) { .check { animation: none; } }
`;
const Mostrar = styled.button`
  flex: none;
  display: grid;
  place-items: center;
  margin-right: 6px;
  padding: 6px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #9aa6b8;
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { color: #fff; }
  &:focus-visible { outline: 2px solid var(--acento); }
  svg { width: 19px; height: 19px; }
`;

const Enlace = styled.button`
  padding: 2px 0;
  border: 0;
  background: none;
  color: var(--acento);
  font: inherit;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  text-underline-offset: 3px;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; border-radius: 2px; }
`;

const Principal = styled.button`
  width: 100%;
  margin-top: 8px;
  height: 50px;
  border: 0;
  border-radius: 9px;
  background: var(--acento);
  color: ${p => p.$texto};
  font: inherit;
  font-size: 1rem;
  font-weight: 650;
  cursor: pointer;
  transition: filter 0.15s, transform 0.1s;
  &:hover:not(:disabled) { filter: brightness(1.08); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.6; cursor: default; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
`;

const Aviso = styled.p`
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.88rem;
  line-height: 1.4;
  ${p => p.$tipo === 'error'
    ? css`background: rgba(240, 122, 122, 0.1); color: #ffb3b3;`
    : css`background: rgba(255, 255, 255, 0.06); color: #c3ccd9;`}
`;

const Pie = styled.p`
  margin: 22px 0 0;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  text-align: center;
  font-size: 0.9rem;
  color: #9aa6b8;
`;

// En la página de acceso, «¿No puedes entrar?» es un bloque propio bajo el formulario (antes era
// solo un enlace, y el aviso vivía en el encabezado). En el modal sigue siendo el enlace simple.
const Rescate = styled.div`
  margin-top: 22px;
  padding: 16px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 14px;
  align-items: center;
  border-radius: 14px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--acento) 12%, transparent), rgba(255, 255, 255, 0.03) 65%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 28%, transparent);
  .candado {
    grid-row: span 2;
    display: grid; place-items: center;
    width: 44px; height: 44px; border-radius: 12px;
    color: var(--acento);
    background: color-mix(in srgb, var(--acento) 18%, transparent);
    svg { width: 22px; height: 22px; }
  }
  b { color: #f3f4f6; font-size: 0.98rem; font-weight: 650; }
  p { margin: 0; color: #a7b0bc; font-size: 0.86rem; line-height: 1.45; }
  button {
    grid-column: 1 / -1;
    margin-top: 12px;
    height: 44px;
    border-radius: 9px;
    border: 1px solid color-mix(in srgb, var(--acento) 60%, transparent);
    background: transparent;
    color: #fff;
    font: inherit; font-size: 0.95rem; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    &:hover { background: color-mix(in srgb, var(--acento) 16%, transparent); border-color: var(--acento); }
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
  }
  .ayuda { grid-column: 1 / -1; margin-top: 12px; text-align: center; font-size: 0.85rem; }
`;

const Sobre = styled.div`
  width: 58px;
  height: 58px;
  margin-bottom: 20px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--acento);
  background: color-mix(in srgb, var(--acento) 14%, transparent);
  svg { width: 26px; height: 26px; }
`;

const IcoCerrar = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M3 3l10 10M13 3L3 13" />
  </svg>
);

const IcoSobre = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 6.5 12 13l8.5-6.5" />
  </svg>
);

// Con `enPagina` no es modal: pinta el mismo panel dentro de la página de acceso
// (sección `acceso_streaming`), sin fondo ni cierre, y las pestañas cambian de
// dirección (/login ↔ /register). `textos` cambia títulos y textos de cada vista.
const SV = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
const IcoCorreo = () => <svg {...SV}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></svg>;
const IcoDoc = () => <svg {...SV}><rect x="3" y="5" width="18" height="14" rx="2.5" /><circle cx="9" cy="11" r="2" /><path d="M6 16c.6-1.4 1.7-2 3-2s2.4.6 3 2M14.5 10H18M14.5 13.5H17" /></svg>;
const IcoCandado = () => <svg {...SV}><rect x="4.5" y="10.5" width="15" height="10" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>;
const IcoPersona = () => <svg {...SV}><circle cx="12" cy="8" r="4" /><path d="M4.5 20.5c.9-3.6 3.9-6 7.5-6s6.6 2.4 7.5 6" /></svg>;
const IcoOjo = ({ tachado }) => <svg {...SV}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" />{tachado && <path d="M4 4l16 16" />}</svg>;
const IcoAlerta = () => <svg {...SV}><path d="M12 3 2.5 20h19Z" /><path d="M12 10v4M12 17h.01" /></svg>;
const CLAVE_CORREO = 'acceso_ultimo_correo';

const AccesoIconoModal = ({ abierto: abiertoProp, onCerrar = () => {}, vistaInicial = 'entrar', onRegistroExterno, acento = '#5ee0d2', textoAcento = '#04201d', fondo = '#060b14', fuente, destino, enPagina = false, textos = {} }) => {
  const abierto = enPagina || abiertoProp;
  const navigate = useNavigate();
  const { login } = useAuth();
  const panelRef = useRef(null);
  const primeraVista = useRef(false);

  const [vista, setVista] = useState(enPagina ? vistaInicial : 'entrar'); // entrar | registro | olvido | enviado
  const [correo, setCorreo] = useState(() => { try { return localStorage.getItem(CLAVE_CORREO) || ''; } catch { return ''; } });
  const [mayus, setMayus] = useState(false);
  const [quien, setQuien] = useState('');
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [registroConfig, setRegistroConfig] = useState(null);
  const [nombre, setNombre] = useState('');
  const [aviso, setAviso] = useState('');

  // Variantes de acceso del proyecto: con documento o sin contraseña.
  useEffect(() => {
    if (!abierto || registroConfig) return undefined;
    let vivo = true;
    axios.get(`${API_BASE_URL}/public-groups.php`)
      .then(res => { if (vivo) setRegistroConfig(res.data?.registro_config || {}); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [abierto, registroConfig]);
  const porDni = !!registroConfig?.login_por_dni;
  const sinClave = !!registroConfig?.login_sin_password;
  // El registro va aquí mismo solo si el proyecto no pide datos extra (campos,
  // casillas obligatorias o documento); si los pide, se abre el formulario completo.
  const registroSimple = !!registroConfig && !porDni && !sinClave
    && !(registroConfig.campos || []).some(c => c && c.visible)
    && !(registroConfig.checks || []).some(c => c && (c.requerido || c.obligatorio));

  // Al abrir: vista de entrada limpia, sin scroll detrás y con el foco dentro.
  useEffect(() => {
    if (!abierto) return undefined;
    setVista(vistaInicial); setError(''); setAviso(''); setMensaje(''); setClave(''); setVerClave(false);
    if (enPagina) return undefined;
    const previo = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (previo && previo.focus) previo.focus();
    };
  }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cada vista pone el foco en su primer campo (o en su botón).
  useEffect(() => {
    if (!abierto) return;
    // En la página, al llegar solo se enfoca en pantallas grandes (en el celular
    // abriría el teclado encima del banner); al cambiar de vista, siempre.
    if (enPagina && !primeraVista.current) { primeraVista.current = true; if (window.innerWidth < 900) return; }
    // El primer campo vacío (si ya está el correo recordado, va directo a la contraseña).
    const campos = [...(panelRef.current?.querySelectorAll('input, [data-foco]') || [])];
    const el = campos.find(x => x.tagName !== 'INPUT' || !x.value) || campos[0];
    if (el) el.focus();
  }, [abierto, vista]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!abierto) return null;

  const onTecla = (e) => {
    if (e.key === 'Escape') { onCerrar(); return; }
    if (e.key !== 'Tab') return;
    // El foco no sale del modal.
    const focos = panelRef.current.querySelectorAll('button, input, a[href]');
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];
    if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  };

  const irA = (v) => { setError(''); setAviso(''); setVista(v); };

  const elegirPestana = (v) => {
    if (v === 'registro' && registroConfig && !registroSimple && onRegistroExterno) { onCerrar(); onRegistroExterno(); return; }
    if (enPagina) { navigate({ pathname: v === 'registro' ? '/register' : '/login', search: window.location.search.replace(/[?&]vista=[^&]*/, '') }); }
    irA(v);
  };

  const crearCuenta = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setError(''); setAviso('');
    if (clave.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    setEnviando(true);
    try {
      const res = await authService.register({ nombre: nombre.trim(), email: correo.trim(), password: clave });
      login({ token: res.data.token, user: res.data.user });
      onCerrar();
      if (destino) navigate(destino);
    } catch (err) {
      const d = err.response?.data || {};
      if (d.code === 'ya_registrado') {
        // Ya es de esta plataforma: a iniciar sesión con el correo puesto.
        setVista('entrar'); setClave('');
        setAviso('Ya tienes una cuenta aquí con este correo. Inicia sesión.');
      } else {
        setError(d.message || 'No se pudo crear la cuenta. Inténtalo de nuevo.');
      }
      setEnviando(false);
    }
  };

  const entrar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setError('');
    setEnviando(true);
    try {
      const res = await authService.login(
        sinClave ? { email: correo.trim(), passwordless: true } : { email: correo.trim(), password: clave }
      );
      try { localStorage.setItem(CLAVE_CORREO, correo.trim()); } catch { /* sin almacenamiento */ }
      if (enPagina) {
        // En la página: un instante de «¡Hola, …!» y luego al destino.
        setQuien((res.data.user?.nombre || res.data.user?.name || '').split(' ')[0]);
        setVista('bienvenida');
        setTimeout(() => { login({ token: res.data.token, user: res.data.user }); navigate(destino || '/'); }, 900);
        return;
      }
      login({ token: res.data.token, user: res.data.user });
      onCerrar();
      if (destino) navigate(destino);
    } catch {
      setError(porDni
        ? 'El documento o la contraseña no coinciden. Revísalos e inténtalo de nuevo.'
        : 'El correo o la contraseña no coinciden. Revísalos e inténtalo de nuevo.');
      setEnviando(false);
    }
  };

  const pedirEnlace = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setError('');
    setEnviando(true);
    try {
      const res = await recuperarContrasenaService.solicitar(correo.trim());
      setMensaje(res.data?.message || 'Si hay una cuenta con ese correo, en unos minutos te llegará el enlace.');
      setVista('enviado');
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo enviar el enlace. Inténtalo de nuevo en unos minutos.');
    } finally {
      setEnviando(false);
    }
  };

  const etiquetaId = porDni ? 'Número de identificación' : 'Correo electrónico';
  const campoId = (
    <Campo>
      <span>{etiquetaId}</span>
      <Caja $error={!!error}>
        <Ico>{porDni ? <IcoDoc /> : <IcoCorreo />}</Ico>
        <input
          type={porDni ? 'text' : 'email'}
          inputMode={porDni ? 'numeric' : 'email'}
          autoComplete={porDni ? 'username' : 'email'}
          placeholder={porDni ? '' : 'tu@correo.com'}
          value={correo}
          onChange={e => setCorreo(e.target.value)}
          required
        />
      </Caja>
    </Campo>
  );

  const contenido = (
      <Panel
        ref={panelRef}
        role={enPagina ? undefined : 'dialog'}
        aria-modal={enPagina ? undefined : 'true'}
        aria-labelledby="acceso-titulo"
        onKeyDown={enPagina ? undefined : onTecla}
        $acento={acento}
        $fondo={fondo}
        $fuente={fuente}
        $enPagina={enPagina}
      >
        {!enPagina && <Cerrar type="button" onClick={onCerrar} aria-label="Cerrar"><IcoCerrar /></Cerrar>}

        {(vista === 'registro' || (vista === 'entrar' && !textos.ocultar_registro)) && (
          <Pestanas role="tablist" aria-label="Acceso" $enPagina={enPagina}>
            <button type="button" role="tab" aria-selected={vista === 'entrar'} onClick={() => elegirPestana('entrar')}>Iniciar sesión</button>
            <button type="button" role="tab" aria-selected={vista === 'registro'} onClick={() => elegirPestana('registro')}>Crear cuenta</button>
          </Pestanas>
        )}

        {vista === 'entrar' && (
          <Vista key="entrar">
            <Titulo id="acceso-titulo">{textos.titulo_entrar || 'Sigue donde lo dejaste'}</Titulo>
            <Texto>{textos.texto_entrar || 'Entra con tu cuenta para ver tus cursos y retomar la clase en el minuto exacto.'}</Texto>
            <form onSubmit={entrar} noValidate={porDni}>
              {campoId}
              {!sinClave && (
                <>
                  <Campo>
                    <span>Contraseña</span>
                    <Caja $error={!!error}>
                      <Ico><IcoCandado /></Ico>
                      <input
                        type={verClave ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={clave}
                        onChange={e => setClave(e.target.value)}
                        onKeyUp={e => setMayus(!!e.getModifierState?.('CapsLock'))}
                        onBlur={() => setMayus(false)}
                        required
                      />
                      <Mostrar type="button" onClick={() => setVerClave(v => !v)} aria-pressed={verClave} aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'} title={verClave ? 'Ocultar' : 'Mostrar'}>
                        <IcoOjo tachado={verClave} />
                      </Mostrar>
                    </Caja>
                    {mayus && <Mayus role="status"><IcoAlerta />Tienes activadas las mayúsculas</Mayus>}
                  </Campo>
                </>
              )}
              {aviso && <Aviso role="status">{aviso}</Aviso>}
              {error && <Aviso $tipo="error" role="alert">{error}</Aviso>}
              <Principal type="submit" disabled={enviando} $texto={textoAcento}>
                {enviando ? <><Giro aria-hidden="true" />Entrando…</> : 'Iniciar sesión'}
              </Principal>
            </form>
            {!sinClave && enPagina && textos.olvido_destacado !== false && (
              <Rescate>
                <span className="candado" aria-hidden="true"><IcoCandado /></span>
                <b>{textos.olvido_titulo || '¿No puedes entrar?'}</b>
                <p>{textos.olvido_texto || 'Te enviamos a tu correo un enlace para crear una contraseña nueva.'}</p>
                <button type="button" onClick={() => irA('olvido')}>{textos.olvido_boton || 'Recuperar contraseña'}</button>
                {textos.ayuda_entrar && <span className="ayuda">{textos.ayuda_entrar}</span>}
              </Rescate>
            )}
            {!sinClave && !(enPagina && textos.olvido_destacado !== false) && (
              <Pie>
                <Enlace type="button" onClick={() => irA('olvido')}>Olvidé mi contraseña</Enlace>
                {textos.ayuda_entrar && <span style={{ display: 'block', marginTop: 10, fontSize: '0.85rem' }}>{textos.ayuda_entrar}</span>}
              </Pie>
            )}
          </Vista>
        )}

        {vista === 'registro' && (
          <Vista key="registro">
            <Titulo id="acceso-titulo">{textos.titulo_registro || 'Empieza hoy'}</Titulo>
            <Texto>{textos.texto_registro || 'Crea tu cuenta para inscribirte en los cursos y seguir tu avance desde cualquier dispositivo.'}</Texto>
            <form onSubmit={crearCuenta}>
              <Campo>
                <span>Nombre completo</span>
                <Caja>
                  <Ico><IcoPersona /></Ico>
                  <input type="text" autoComplete="name" value={nombre} onChange={e => setNombre(e.target.value)} required />
                </Caja>
              </Campo>
              <Campo>
                <span>Correo electrónico</span>
                <Caja $error={!!error}>
                  <Ico><IcoCorreo /></Ico>
                  <input type="email" inputMode="email" autoComplete="email" placeholder="tu@correo.com"
                    value={correo} onChange={e => setCorreo(e.target.value)} required />
                </Caja>
              </Campo>
              <Campo>
                <span>Contraseña</span>
                <Caja $error={!!error}>
                  <Ico><IcoCandado /></Ico>
                  <input type={verClave ? 'text' : 'password'} autoComplete="new-password" minLength={6}
                    value={clave} onChange={e => setClave(e.target.value)} required />
                  <Mostrar type="button" onClick={() => setVerClave(v => !v)} aria-pressed={verClave} aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'} title={verClave ? 'Ocultar' : 'Mostrar'}>
                    <IcoOjo tachado={verClave} />
                  </Mostrar>
                </Caja>
              </Campo>
              {error && (
                <Aviso $tipo="error" role="alert">
                  {error}
                  {/otra plataforma de Ateneo/.test(error) && (
                    <> <Enlace type="button" onClick={() => irA('olvido')}>Olvidé mi contraseña</Enlace></>
                  )}
                </Aviso>
              )}
              <Principal type="submit" disabled={enviando} $texto={textoAcento}>
                {enviando ? <><Giro aria-hidden="true" />Creando tu cuenta…</> : 'Crear cuenta'}
              </Principal>
            </form>
            <Pie>¿Ya estudias en otra plataforma de Ateneo? Usa el mismo correo y contraseña.</Pie>
            {textos.legal_registro && <Pie style={{ marginTop: 12, paddingTop: 0, borderTop: 0, fontSize: '0.82rem' }}>{textos.legal_registro}</Pie>}
          </Vista>
        )}

        {vista === 'olvido' && (
          <Vista key="olvido">
            <Titulo id="acceso-titulo">Crea una contraseña nueva</Titulo>
            <Texto>
              Escribe {porDni ? 'el correo o el documento' : 'el correo'} de tu cuenta y te enviamos un enlace
              para crear una contraseña nueva. El enlace dura una hora.
            </Texto>
            <form onSubmit={pedirEnlace}>
              {campoId}
              {error && <Aviso $tipo="error" role="alert">{error}</Aviso>}
              <Principal type="submit" disabled={enviando} $texto={textoAcento}>
                {enviando ? <><Giro aria-hidden="true" />Enviando…</> : 'Enviar enlace'}
              </Principal>
            </form>
            <Pie>
              <Enlace type="button" onClick={() => irA('entrar')}>Volver a iniciar sesión</Enlace>
            </Pie>
          </Vista>
        )}

        {vista === 'bienvenida' && (
          <Vista key="bienvenida">
            <Bienvenida role="status">
              <div className="check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg></div>
              <Titulo id="acceso-titulo">{quien ? `¡Hola, ${quien}!` : '¡Bienvenido!'}</Titulo>
              <Texto>Abriendo tus cursos…</Texto>
            </Bienvenida>
          </Vista>
        )}

        {vista === 'enviado' && (
          <Vista key="enviado">
            <Sobre><IcoSobre /></Sobre>
            <Titulo id="acceso-titulo">Revisa tu correo</Titulo>
            <Texto role="status">{mensaje}</Texto>
            <Principal type="button" data-foco onClick={() => irA('entrar')} $texto={textoAcento}>
              Volver a iniciar sesión
            </Principal>
          </Vista>
        )}
      </Panel>
  );
  if (enPagina) return contenido;
  return createPortal(
    <Fondo onMouseDown={e => { if (e.target === e.currentTarget) onCerrar(); }}>{contenido}</Fondo>,
    document.body
  );
};

export default AccesoIconoModal;
